import { memo, useCallback, useDeferredValue, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@mui/icons-material/Search';
import { Stack, ThemeProvider } from '@mui/material';

import { layerTheme } from '@/modules/common/theme';
import { TextInput } from '@/modules/common/components/inputs';
import {
  createVehicleGeneratedLayerName,
  isGeneratedLayerNames,
  LayerName,
  LayerNames,
} from '@/modules/common/types/layers';
import { Accordion } from '@common/components/Accordion';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';

import { LayerItem } from './LayerItem';
import type { LayerElement, LayerGroup } from './LayerItem';
import { UnhideEverythingButton } from './UnhideEverythingButton';
import { useLayerStore } from '../store/useLayerStore';
import { FDDM_LAYER_TREE, getLayerTreeItemKey, isLayerDefinition } from '../layerTree';
import type { LayerTreeItem } from '../layerTree';
import { UserPreferenceName } from '@/modules/userPreferences';
import {
  FIELDS_PADDING_BOTTOM,
  PANEL_ACCORDION_SX,
} from '@/modules/common/constants/propertyPanel';

type LayerElementsByName = Partial<Record<LayerNames, LayerElement[]>>;

const EMPTY_ELEMENTS: LayerElement[] = [];

const isVehicleLayerTreeItem = (item: LayerTreeItem): boolean => {
  if (isLayerDefinition(item)) return isGeneratedLayerNames(item.name);

  return item.children.some(isVehicleLayerTreeItem);
};

const VEHICLE_LAYER_TREE_ITEMS = FDDM_LAYER_TREE.filter(isVehicleLayerTreeItem);
const SHARED_LAYER_TREE_ITEMS = FDDM_LAYER_TREE.filter((item) => !isVehicleLayerTreeItem(item));
const isStationLayerTreeItem = (item: LayerTreeItem): boolean =>
  isLayerDefinition(item) && item.name === LayerNames.GENERATED_STATIONS;
const STATION_LAYER_TREE_ITEMS = SHARED_LAYER_TREE_ITEMS.filter(isStationLayerTreeItem);
const OTHER_SHARED_LAYER_TREE_ITEMS = SHARED_LAYER_TREE_ITEMS.filter(
  (item) => !isStationLayerTreeItem(item),
);

const useLayerElementsByName = (): LayerElementsByName => {
  const singularStationAreas = useFloorplanDesignStore(
    (state) => state.design.areas.singularStationAreas,
  );
  const stationaryAreas = useFloorplanDesignStore((state) => state.design.areas.stationaryAreas);
  const highwayAreas = useFloorplanDesignStore((state) => state.design.areas.highwayAreas);
  const rackingAreas = useFloorplanDesignStore((state) => state.design.areas.rackingAreas);
  const manualAreas = useFloorplanDesignStore((state) => state.design.areas.manualAreas);
  const obstacles = useFloorplanDesignStore((state) => state.design.obstacles);
  const flows = useFloorplanDesignStore((state) => state.design.flows);
  const logicalSpaces = useFloorplanDesignStore((state) => state.design.logicalSpaces);
  const groups = useFloorplanDesignStore((state) => state.uxonly.groups);

  return useMemo(() => {
    const highways = highwayAreas.filter((area) => area.segments.length === 1);
    const angledHighways = highwayAreas.filter((area) => area.segments.length > 1);
    const walls = obstacles.filter((obstacle) => obstacle.appearance === 'WALL');
    const nonWallObstacles = obstacles.filter((obstacle) => obstacle.appearance !== 'WALL');

    const intakeAreas = [
      ...singularStationAreas.filter((area) => area.loadHandlingType === 'INTAKE'),
      ...rackingAreas.filter((area) => area.rackingLoadHandlingType === 'INTAKE'),
    ];
    const deliveryAreas = [
      ...singularStationAreas.filter((area) => area.loadHandlingType === 'DELIVERY'),
      ...rackingAreas.filter((area) => area.rackingLoadHandlingType === 'DELIVERY'),
    ];
    const storageAreas = [
      ...singularStationAreas.filter((area) => area.loadHandlingType === 'STORAGE'),
      ...rackingAreas.filter((area) => area.rackingLoadHandlingType === 'STORAGE'),
    ];
    const chargingAreas = stationaryAreas.filter((area) => area.stationaryType === 'CHARGING');
    const parkingAreas = stationaryAreas.filter((area) => area.stationaryType === 'PARKING');
    const extractionAreas = stationaryAreas.filter((area) => area.stationaryType === 'EXTRACTION');
    const insertionAreas = stationaryAreas.filter((area) => area.stationaryType === 'INSERTION');
    const processAreas = [
      ...singularStationAreas.filter((area) => area.loadHandlingType === 'OFF_VEHICLE_PROCESSING'),
      ...singularStationAreas.filter((area) => area.loadHandlingType === 'ON_VEHICLE_PROCESSING'),
    ];
    const manualAreaElements = [
      ...manualAreas,
      ...singularStationAreas.filter((area) => area.loadHandlingType === 'INTAKE_AND_DELIVERY'),
    ];

    return {
      [LayerNames.FLOWS]: flows.flows,
      [LayerNames.WALLS]: walls,
      [LayerNames.OBSTACLES]: nonWallObstacles,
      [LayerNames.HIGHWAYS]: highways,
      [LayerNames.ANGLED_HIGHWAYS]: angledHighways,
      [LayerNames.INTAKE]: intakeAreas,
      [LayerNames.DELIVERY]: deliveryAreas,
      [LayerNames.STORAGE]: storageAreas,
      [LayerNames.CHARGING]: chargingAreas,
      [LayerNames.PARKING]: parkingAreas,
      [LayerNames.MANUAL]: manualAreaElements,
      [LayerNames.EXTRACTION]: extractionAreas,
      [LayerNames.INSERTION]: insertionAreas,
      [LayerNames.PROCESS]: processAreas,
      [LayerNames.LOGICAL_SPACES]: logicalSpaces,
      [LayerNames.GROUPS]: groups,
    };
  }, [
    highwayAreas,
    obstacles,
    singularStationAreas,
    rackingAreas,
    stationaryAreas,
    manualAreas,
    flows.flows,
    logicalSpaces,
    groups,
  ]);
};

const LayerPropertiesContentComponent = () => {
  const { t } = useTranslation('interface');
  const elementsByLayerName = useLayerElementsByName();
  const vehicleTypes = useFloorplanDesignStore((state) => state.setup.vehicleTypes);
  const vehicleLayouts = useFloorplanDesignStore((state) => state.floorplan.vehicleLayouts);

  const [searchQuery, setSearchQuery] = useState('');
  const deferredSearchQuery = useDeferredValue(searchQuery);

  const normalizedSearchQuery = useMemo(
    () => deferredSearchQuery.trim().toLocaleLowerCase(),
    [deferredSearchQuery],
  );

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const layerGroups = useMemo<LayerGroup[]>(() => {
    const createLayerGroup = (definition: LayerTreeItem, vehicleTypeId?: string): LayerGroup => {
      let name: LayerName | undefined;
      let elements = EMPTY_ELEMENTS;

      if (isLayerDefinition(definition)) {
        name = definition.name;

        if (vehicleTypeId && isGeneratedLayerNames(definition.name)) {
          name = createVehicleGeneratedLayerName(vehicleTypeId, definition.name);

          const isPoiLeaf =
            definition.name === LayerNames.GENERATED_POIS && !definition.children?.length;

          if (isPoiLeaf) {
            elements = vehicleLayouts
              .filter((vehicleLayout) => vehicleLayout.vehicleTypeId === vehicleTypeId)
              .flatMap((vehicleLayout) => vehicleLayout.pois);
          }
        }

        if (!vehicleTypeId) {
          elements = elementsByLayerName[definition.name] ?? EMPTY_ELEMENTS;
        }
      }

      return {
        id: vehicleTypeId
          ? `${vehicleTypeId}-${getLayerTreeItemKey(definition)}`
          : getLayerTreeItemKey(definition),
        name,
        label: t(definition.labelKey, definition.fallbackLabel),
        elements,
        groups:
          definition.children?.map((childDefinition) =>
            createLayerGroup(childDefinition, vehicleTypeId),
          ) ?? [],
      };
    };

    const createVehicleRootLayerGroups = (): LayerGroup[] => {
      const singleVehicleType = vehicleTypes[0];

      if (vehicleTypes.length === 1 && singleVehicleType) {
        return VEHICLE_LAYER_TREE_ITEMS.map((definition) =>
          createLayerGroup(definition, singleVehicleType.id),
        );
      }

      return vehicleTypes.map((vehicleType) => ({
        id: `vehicle-${vehicleType.id}`,
        label: vehicleType.id,
        groups: VEHICLE_LAYER_TREE_ITEMS.map((definition) =>
          createLayerGroup(definition, vehicleType.id),
        ),
      }));
    };

    const vehicleRootLayerGroups = createVehicleRootLayerGroups();

    return [
      ...STATION_LAYER_TREE_ITEMS.map((definition) => createLayerGroup(definition)),
      ...vehicleRootLayerGroups,
      ...OTHER_SHARED_LAYER_TREE_ITEMS.map((definition) => createLayerGroup(definition)),
    ];
  }, [elementsByLayerName, t, vehicleLayouts, vehicleTypes]);

  const searchLabel = t('interface:layers.search', 'Search layers');

  return (
    <ThemeProvider theme={layerTheme}>
      <Stack direction='column' spacing={1} pb={FIELDS_PADDING_BOTTOM}>
        <TextInput
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder={searchLabel}
          ariaLabel={searchLabel}
          prefix={<SearchIcon fontSize='small' />}
          debounceMs={0}
        />

        <Stack direction='column' spacing={0} sx={{ marginRight: -2 }}>
          {layerGroups.map((layerGroup) => (
            <LayerItem
              key={layerGroup.id}
              name={layerGroup.name}
              label={layerGroup.label}
              level={0}
              elements={layerGroup.elements}
              groups={layerGroup.groups}
              searchQuery={normalizedSearchQuery}
            />
          ))}
        </Stack>
      </Stack>
    </ThemeProvider>
  );
};

const LayerPropertiesContent = memo(LayerPropertiesContentComponent);

const LayerPropertiesComponent = () => {
  const { t } = useTranslation('interface');
  const isEverythingVisible = useLayerStore((state) => state.isEverythingVisible());
  const showUnhideButton = !isEverythingVisible;

  return (
    <Accordion
      persistId={UserPreferenceName.ACCORDION_PROPERTY_PANEL_LAYERS}
      title={t('interface:layers.toggle', 'Layers')}
      afterIcon={showUnhideButton ? <UnhideEverythingButton /> : null}
      unmountOnExit
      sx={{ ...PANEL_ACCORDION_SX, fontSize: '14px' }}
    >
      <LayerPropertiesContent />
    </Accordion>
  );
};

export const LayerProperties = memo(LayerPropertiesComponent);
